import { addDays } from "date-fns"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useMutation } from "react-query"
import {
  handleViewDocument,
  modalRenderDocument,
  validateExpirationDate,
} from "src/main/Account/Boats/SharedMethods"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import {
  removeInsuranceAttachment,
  updateInsuranceDetails,
} from "src/api/Account/Boats"

import { useToast } from "src/hooks/use_toast"
import useWindowSize from "src/hooks/use_window_size"

const InsuranceModal = ({ isOpen, boat, setIsOpen, setBoatData }) => {
  const showToast = useToast()
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false)
  const [file, setFile] = useState(null)
  const { isLargeScreen } = useWindowSize()
  const documentUrl =
    boat.insurance?.documentPreviewImageUrl || boat.insurance?.documentUrl

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useFormContext()

  const handleSelectDocument = (document) => {
    setFile(document)
    setIsFileSelectorOpen(false)
  }

  const handleClose = () => {
    if (file) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        setIsOpen(false)
        setFile(null)
      }
    } else {
      setIsOpen(false)
      setFile(null)
    }
  }

  const {
    mutate: removeInsuranceDocument,
    isLoading: removingInsuranceDocument,
  } = useMutation(() => removeInsuranceAttachment(boat.encodedId), {
    onSuccess: (data) => {
      showToast("Insurance document successfully removed!", { type: "success" })
      setFile(null)
      setBoatData(data.boat)
    },
    onError: (error) => {
      showToast(error.message, {
        type: "error",
      })
    },
  })

  const { mutate: updateInsurance, isLoading: updatingInsurance } = useMutation(
    (data) => updateInsuranceDetails({ data, boatId: boat.encodedId, file }),
    {
      onSuccess: (data) => {
        showToast("Insurance successfully updated!", { type: "success" })
        setIsOpen(false)
        setFile(null)
        setBoatData(data.boat)
      },
      onError: (error) => {
        showToast(error.message, {
          type: "error",
        })
      },
    }
  )

  const handleRemoveDocument = () => {
    setFile(null)

    if (boat.insurance?.documentEncodedId) {
      removeInsuranceDocument(boat.insurance.documentEncodedId)
    }
  }

  const submitButtonText = () => {
    if (updatingInsurance) {
      return "Saving"
    } else {
      return "Save"
    }
  }

  const onSubmit = (data) => {
    updateInsurance(data)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header title="Insurance" />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="mb-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 space-y-2 rounded border p-4 shadow-md md:col-span-6">
              <div className="flex w-full justify-between">
                <span className="font-semibold">Document</span>
                {boat.insurance?.documentUrl && (
                  <Button
                    small
                    variant="ghost"
                    onClick={() =>
                      handleViewDocument(boat.insurance?.documentUrl)
                    }
                  >
                    View
                  </Button>
                )}
              </div>
              {modalRenderDocument(file, documentUrl)}
              <div className="w-full flex-col space-y-1">
                <Button
                  variant="secondary"
                  onClick={() => setIsFileSelectorOpen(true)}
                  fullWidth
                >
                  {file || documentUrl ? "Replace" : "Upload"}
                </Button>
                {file || documentUrl ? (
                  <Button
                    onClick={handleRemoveDocument}
                    fullWidth
                    isLoading={removingInsuranceDocument}
                  >
                    {removingInsuranceDocument ? "Deleting" : "Delete"}
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="col-span-12 flex flex-col space-y-2 md:col-span-6">
              <div className="flex flex-col space-y-1">
                <Form.Label htmlFor="companyName">Company</Form.Label>
                <Form.TextField
                  id="companyName"
                  {...register("insurance.companyName")}
                />
              </div>
              <div className="flex flex-col space-y-1">
                <Form.Label htmlFor="policyNumber">Policy Number</Form.Label>
                <Form.TextField
                  id="policyNumber"
                  {...register("insurance.policyNumber")}
                />
              </div>
              <div className="flex flex-col space-y-1">
                <Form.Label htmlFor="expirationDate">
                  Expiration Date
                </Form.Label>
                <Controller
                  name="insurance.expirationDate"
                  control={control}
                  rules={{ validate: validateExpirationDate }}
                  render={({ field: { onChange, value } }) => (
                    <Form.DatePicker
                      id="expirationDate"
                      minDate={addDays(new Date(), 1)}
                      renderCustomHeader={(props) => (
                        <Form.DatePicker.QuickNavHeader {...props} />
                      )}
                      hasErrors={!!errors.insurance?.expirationDate}
                      {...{ onChange, value }}
                    />
                  )}
                />
                <Form.Error>
                  {errors.insurance?.expirationDate?.message}
                </Form.Error>
              </div>
            </div>
          </div>
          <Form.FileSelector
            isOpen={isFileSelectorOpen}
            onSelect={handleSelectDocument}
            onClose={() => {
              setIsFileSelectorOpen(false)
              setFile(null)
            }}
            dragAndDropEnabled={isLargeScreen}
            isLoading={updatingInsurance}
          />
        </Modal.Body>
        <Modal.Footer
          onClose={handleClose}
          confirmBtnText={submitButtonText()}
          confirmBtnVariant="primary"
          confirmBtnLoading={updatingInsurance}
          confirmBtnType="submit"
          onSubmit={handleSubmit(onSubmit)}
          cancelBtnText="Cancel"
          disableCancelBtn={updatingInsurance}
        />
      </Form>
    </Modal>
  )
}

InsuranceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  boat: PropTypes.object.isRequired,
  setBoatData: PropTypes.func.isRequired,
}

export default InsuranceModal
